import { useQueryClient } from '@tanstack/react-query';
import { commonClient } from 'api';
import type { AppUser, UserPermission } from 'api/models/mainservice';
import axios from 'axios';
import SuspenseLoadingIndicator from 'components/LoadingPageIndicator/SuspenseLoadingIndicator';
import dayjs from 'dayjs';
import { getDevicePlatform, getDeviceType } from 'helpers/deviceHelper';
import { handleApiError } from 'helpers/errorHandling';
import {
  clearUserFromSentry,
  setLanguageToSentry,
  setUserToSentry
} from 'helpers/sentryExtensions';
import {
  clearTenantCode,
  getTenantCode,
  LOCALSTORAGE_TENANT_CODE,
  saveTenantCode
} from 'helpers/tenantCode';
import { getDayOfWeekNumber } from 'helpers/timeHelpers';
import {
  deleteTokens,
  getAccessToken,
  getDeviceId,
  getRefreshToken,
  LOCALSTORAGE_ACCESS_TOKEN_KEY,
  LOCALSTORAGE_REFRESH_TOKEN_KEY,
  saveAccessToken,
  saveDownloadUriTemplates,
  saveRefreshToken
} from 'helpers/tokenManager';
import { getLanguageCode, useCustomTranslation } from 'helpers/translationHelper';
import type { SUPPORTED_LANGUAGES_TYPE } from 'i18n';
import { DEFAULT_LANGUAGE } from 'i18n';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useGetCompanyAndUserInfoQuery } from './queries';

// based on https://dev.to/finiam/predictable-react-authentication-with-the-context-api-g10

interface Props {
  children: React.ReactNode;
}

interface LocationState {
  from?: string;
}

interface AuthContextType {
  authenticated: boolean;
  user?: AppUser;
  error?: string;
  tenantCode: string;
  historyStack: string[];
  language?: SUPPORTED_LANGUAGES_TYPE;
  loading: boolean;
  refreshTokenFailed: boolean;
  userPermissions: UserPermission[];
  login: (values: {
    username: string;
    password: string;
    customTenantCode?: string;
  }) => Promise<any>;
  logout: () => Promise<void>;
  setTenant: (values: { tenantCode: string }) => Promise<any>;
  clearTenant: () => void;
  pushUrlToStack: (url: string) => void;
  popUrlFromStack: () => void;
  clearStack: (url: string) => void;
  setLanguage: (code: SUPPORTED_LANGUAGES_TYPE) => void;
  setUser: (user: AppUser) => void;
  handleMissingToken: (e: any) => void;
}

const AuthContext = createContext<AuthContextType>({
  historyStack: [],
  authenticated: false,
  tenantCode: getTenantCode(),
  loading: false,
  refreshTokenFailed: false,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  setTenant: () => Promise.resolve(),
  clearTenant: () => {},
  clearStack: () => {},
  popUrlFromStack: () => {},
  pushUrlToStack: () => {},
  setLanguage: () => {},
  setUser: () => {},
  handleMissingToken: () => {},
  userPermissions: []
});

export const AuthProvider: React.FC<Props> = (props): React.ReactElement => {
  const { children } = props;

  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState<AppUser | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshTokenFailed, setRefreshTokenFailed] = useState(false);
  const [tenantCode, setTenantCode] = useState(getTenantCode());
  const [historyStack, setHistoryStack] = useState<string[]>([]);
  const [loadingInitial, setLoadingInitial] = useState<boolean>(true);
  const [language, setLanguage] = useState<SUPPORTED_LANGUAGES_TYPE | undefined>(
    getLanguageCode() ?? DEFAULT_LANGUAGE
  );
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);

  const { i18n } = useCustomTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;

  const queryClient = useQueryClient();

  const companyAndUserQuery = useGetCompanyAndUserInfoQuery(authenticated);
  const getCurrentPath = useCallback(
    () => `${location.pathname}${location.search}${location.hash}`,
    [location.hash, location.pathname, location.search]
  );

  const logout = useCallback(async () => {
    try {
      await commonClient.signOut();

      deleteTokens();
      clearUserFromSentry();
      setUser(undefined);
      setAuthenticated(false);
      setRefreshTokenFailed(false);
      queryClient.clear();
    } catch (e: any) {
      handleApiError(e);
      setError(e);
    }
  }, [queryClient]);

  // If we change page, reset the error state.
  useEffect(() => {
    if (error) setError(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const initToken = useCallback(async () => {
    try {
      const token = getAccessToken();
      if (token) {
        const result = await commonClient.getCompanyAndUserInfo();

        setUserPermissions(result.userPermissions);
        setUserToSentry(result.user);
        setUser(result.user);
        setRefreshTokenFailed(false);
        setAuthenticated(true);
        setLoadingInitial(false);
      }
    } catch (e) {
      // Only logout for actual auth failures, not network errors
      const isNetworkError =
        axios.isAxiosError(e) &&
        (!e.response ||
          e.code === 'ECONNABORTED' ||
          e.code === 'ERR_NETWORK' ||
          e.code === 'ERR_CANCELED');

      if (!isNetworkError) {
        await logout();
      }
      // On network error, just silently fail - user stays logged in with existing token
    } finally {
      setLoadingInitial(false);
    }
  }, [logout]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    initToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.addEventListener('focus', initToken);
    return () => {
      window.removeEventListener('focus', initToken);
    };
  }, [initToken]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.storageArea !== localStorage) {
        return;
      }

      if (
        event.key === LOCALSTORAGE_ACCESS_TOKEN_KEY ||
        event.key === LOCALSTORAGE_REFRESH_TOKEN_KEY
      ) {
        const accessToken = getAccessToken();
        const refreshToken = getRefreshToken();

        if (!accessToken && !refreshToken) {
          clearUserFromSentry();
          setUser(undefined);
          setAuthenticated(false);
          setRefreshTokenFailed(false);
          queryClient.clear();
          const redirectPath = getCurrentPath();
          navigate('/login', {
            replace: true,
            state: redirectPath.startsWith('/login') ? undefined : { from: redirectPath }
          });
        } else if (!authenticated && event.key === LOCALSTORAGE_ACCESS_TOKEN_KEY && !!accessToken) {
          void initToken();
        }

        return;
      }

      if (event.key === LOCALSTORAGE_TENANT_CODE) {
        setTenantCode(event.newValue || '');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [authenticated, getCurrentPath, initToken, navigate, queryClient]);

  useEffect(() => {
    companyAndUserQuery.data?.user && setUserToSentry(companyAndUserQuery.data?.user);
  }, [companyAndUserQuery.data?.user]);

  useEffect(() => {
    if (location.pathname === '/login' && authenticated) {
      const goBackUrl = locationState?.from;

      if (goBackUrl && !goBackUrl.startsWith('/login')) {
        navigate(goBackUrl, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [locationState, location, navigate, authenticated]);

  const login = useCallback(
    async (values: { username: string; password: string; customTenantCode?: string }) => {
      setLoading(true);
      try {
        const result = await commonClient.signIn({
          password: values.password,
          tenantCode: values.customTenantCode || tenantCode,
          username: values.username,
          deviceInfo: {
            deviceIdentifier: getDeviceId(),
            deviceType: getDeviceType(),
            devicePlatform: getDevicePlatform()
          }
        });

        if (result.data.success && result.data.user) {
          // A new session must never render the previous account's cached preferences.
          queryClient.clear();
          result.data.accessToken && saveAccessToken(result.data.accessToken);
          result.data.refreshToken && saveRefreshToken(result.data.refreshToken);
          result.data.downloadUriTemplates &&
            saveDownloadUriTemplates(result.data.downloadUriTemplates);

          setUserPermissions(result.data.userPermissions);
          setAuthenticated(true);
          setUser(result.data.user);
          setRefreshTokenFailed(false);
          setTenantCode(result.data.user.tenantCode);
          setUserToSentry(result.data.user);

          // Invalidate all queries to have up to date data
          await queryClient.invalidateQueries();
          return undefined;
        }
      } catch (e: any) {
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [queryClient, tenantCode]
  );

  const setTenant = useCallback(async (values: { tenantCode: string }) => {
    try {
      await commonClient.getPublicCompanySettings(values.tenantCode);
      saveTenantCode(values.tenantCode);
      setTenantCode(values.tenantCode);
      return undefined;
    } catch (e: any) {
      setError(e);
      throw e;
    }
  }, []);

  const clearTenant = () => {
    setTenantCode('');
    clearTenantCode();
  };

  // History stack for handling backbutton

  const pushUrlToStack = useCallback(
    (url: string) => setHistoryStack([...historyStack, url]),
    [historyStack]
  );

  const popUrlFromStack = useCallback(
    () => setHistoryStack(historyStack.slice(0, -1)),
    [historyStack]
  );

  const clearStack = useCallback((url: string) => setHistoryStack([url]), []);

  const handleMissingToken = useCallback(
    (error: any) => {
      setAuthenticated(false);
      setError(error);
      setUser(undefined);
      setRefreshTokenFailed(true);
      queryClient.clear();

      const redirectPath = getCurrentPath();
      navigate('/login', {
        replace: true,
        state: redirectPath.startsWith('/login') ? undefined : { from: redirectPath }
      });
    },
    [getCurrentPath, navigate, queryClient]
  );

  const onLanguageChange = useCallback(
    (lng: SUPPORTED_LANGUAGES_TYPE) => {
      setLanguage(lng);
      setLanguageToSentry(lng);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      i18n.changeLanguage(lng);

      let dayJsCustomization = {};

      if (companyAndUserQuery.data?.company) {
        dayJsCustomization = {
          week: {
            dow: getDayOfWeekNumber(companyAndUserQuery.data.company.firstDayOfWeek),
            doy: companyAndUserQuery.data.company.firstDayOfYear
          }
        };
      }

      dayjs.locale(lng);
      dayjs.updateLocale(lng, dayJsCustomization);
    },
    [i18n, companyAndUserQuery.data?.company]
  );

  // Set initial language
  useEffect(() => {
    const savedLanguage = getLanguageCode();
    if (savedLanguage) {
      return onLanguageChange(savedLanguage);
    }

    if (companyAndUserQuery.data?.company.languageId) {
      return onLanguageChange(
        companyAndUserQuery.data.company.languageId.toLocaleLowerCase() as SUPPORTED_LANGUAGES_TYPE
      );
    }

    return onLanguageChange(DEFAULT_LANGUAGE);
  }, [onLanguageChange, companyAndUserQuery.data?.company.languageId]);

  // Make the provider update only when it should.
  // We only want to force re-renders if the user,
  // loading or error states change.
  //
  // Whenever the `value` passed into a provider changes,
  // the whole tree under the provider re-renders, and
  // that can be very costly! Even in this case, where
  // you only get re-renders when logging in and out
  // we want to keep things very performant.
  const memoedValue = useMemo<AuthContextType>(
    () => ({
      user,
      loading,
      error,
      login,
      logout,
      setTenant,
      clearTenant,
      authenticated,
      tenantCode,
      historyStack,
      clearStack,
      popUrlFromStack,
      pushUrlToStack,
      language,
      setLanguage: onLanguageChange,
      handleMissingToken,
      setUser,
      refreshTokenFailed,
      userPermissions
    }),
    [
      user,
      loading,
      error,
      login,
      logout,
      setTenant,
      authenticated,
      tenantCode,
      historyStack,
      pushUrlToStack,
      popUrlFromStack,
      clearStack,
      language,
      handleMissingToken,
      refreshTokenFailed,
      userPermissions,
      onLanguageChange
    ]
  );

  // We only want to render the underlying app after we
  // assert for the presence of a current user.
  return (
    <AuthContext.Provider value={memoedValue}>
      {loadingInitial ? <SuspenseLoadingIndicator /> : children}
    </AuthContext.Provider>
  );
};

// Let's only export the `useAuth` hook instead of the context.
// We only want to use the hook directly and never the context component.
export default function useAuth() {
  return useContext(AuthContext);
}
